import type { ReactNode } from 'react';
import { theme } from 'antd';
import { RightOutlined } from '@ant-design/icons';
import type { ColumnsType, TableProps } from 'antd/es/table';

/**
 * Makes rows that open something look clickable before any hover (SF-5146): the first
 * column's cell becomes a link-coloured button (so the row is keyboard reachable; Enter or
 * Space clicks it and the click reaches the row's handler), a narrow muted arrow column is
 * appended, and the row gets a pointer cursor. Pair it with `ClickableRowsHover`
 * (DataTable.tsx) for the hover background. Without `onRowClick` the columns come back
 * unchanged.
 */
export function useClickableRows<T>(
  columns: ColumnsType<T>,
  onRowClick?: (record: T) => void,
  isRowClickable: (record: T) => boolean = () => true,
): { columns: ColumnsType<T>; onRow: TableProps<T>['onRow'] } {
  const { token } = theme.useToken();
  if (!onRowClick || columns.length === 0) return { columns, onRow: undefined };

  const [first, ...rest] = columns;
  const linkFirst =
    'children' in first
      ? first
      : {
          ...first,
          render: (value: unknown, record: T, index: number) => {
            const cell = (first.render ? first.render(value, record, index) : value) as ReactNode;
            if (!isRowClickable(record)) return cell;
            return (
              <button
                type="button"
                style={{
                  padding: 0,
                  border: 0,
                  background: 'none',
                  font: 'inherit',
                  textAlign: 'inherit',
                  color: token.colorLink,
                  cursor: 'pointer',
                }}
              >
                {cell}
              </button>
            );
          },
        };
  const arrowColumn: ColumnsType<T>[number] = {
    key: 'open-row',
    width: 32,
    align: 'right',
    render: (_value, record) =>
      isRowClickable(record) ? (
        <RightOutlined aria-hidden style={{ color: token.colorTextTertiary }} />
      ) : null,
  };

  return {
    columns: [linkFirst, ...rest, arrowColumn],
    onRow: (record) =>
      isRowClickable(record)
        ? { onClick: () => onRowClick(record), style: { cursor: 'pointer' } }
        : {},
  };
}
